import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { MainService } from '../../../../../../core/services/main.service';
import { ConfirmService } from '../../../../../../shared/components/confirm-box/confirm-box.service';
import { ValidationService } from '../../../../../../shared/components/form-control/validation.service';
import { WorkExperienceService } from '../../work-experience.service';
import { EditWorkExperienceService } from '../edit-work-experience.service';
import { PRISONWORKS_VALIDATION_CONTEXT } from './prison-work.validation';
import { ICanDeactivate } from '../../../../../../core/guards/deactivate.interface';

const PRISONFORM_VALIDATORS_SAVE = {
  section: [Validators.required],
  jobLocation: [Validators.required],
  jobTitle: [Validators.required, Validators.maxLength(100)],
  jobStartDate: [Validators.required, ValidationService.monthYearValidator, ValidationService.futureDate, ValidationService.pastYear(80)],
  jobEndDate: [ValidationService.monthYearValidator,
  ValidationService.invalidEndDateOrCurrent('jobStartDate', 'jobEndDate', 'isJobCurrent'),
  ValidationService.invalidEndDateAndCurrent('jobEndDate', 'isJobCurrent'),
  ValidationService.futureDate],
  otherSectionName: [Validators.required, Validators.maxLength(200)],
  otherSkillDescription: [Validators.required, Validators.maxLength(200), Validators.pattern('[a-zA-Z0-9\\-\\s\&\(\)\/\.\'\,\"\:\;]*')],
}

const PRISONFORM_VALIDATORS_DRAFT = {
  jobStartDate: [ValidationService.monthYearValidator],
  jobEndDate: [ValidationService.monthYearValidator],
  otherSkillDescription: [Validators.maxLength(200)],
  otherSectionName: [Validators.maxLength(200)],
}

const SKILL_DESCRIPTION_VALIDATORS = {
  answers: [ValidationService.minSelected(3), ValidationService.maxSelected(3)],
}

interface IRefData {
  id: number,
  identifier: string,
  description: string,
}

@Component({
  selector: 'vc-prison-work',
  templateUrl: './prison-work.component.html',
  styleUrls: ['./prison-work.component.scss']
})
export class PrisonWorkComponent implements OnInit, ICanDeactivate, OnDestroy {

  prisonForm: FormGroup;
  createAnother = new FormControl('N');

  jobTitle: any = [];
  jobLocation: any = [];
  prisons: string[] = [];
  prisonSections: IRefData[] = [];

  endDateDisabled = false;

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly main: MainService,
    private readonly route: ActivatedRoute,
    private readonly confirm: ConfirmService,
    private readonly validation: ValidationService,
    private readonly workService: WorkExperienceService,
    private readonly editWEService: EditWorkExperienceService,
    private readonly location: Location,
  ) {
    this.validation.setContext(PRISONWORKS_VALIDATION_CONTEXT);
    this.main.pageTitle = this.route.snapshot.data.title;
    this.main.pageMainTitle = 'Work experience';
    this.initForm();
    this.resolveRefData();
    this.route.queryParams.subscribe(params => {
      this.loadSkillsAndResolve(params.id)
    });
    this.editWEService.setFormGroups([this.prisonForm, this.prisonForm.get('skillDescription') as FormGroup]);
  }
  ngOnDestroy(): void {
    this.main.pageMainTitle = '';
  }
  ngOnInit() {
    this.prisonForm.get('skillDescription').get('answers').valueChanges
      .subscribe((data: any[]) => {
        const filtered = data.filter(each => each.checked);
        if (filtered.find(each => each.id === '20')) {
          this.prisonForm.addControl('otherSkillDescription', this.fb.control(''));
        } else {
          this.prisonForm.removeControl('otherSkillDescription');
        }
      });

    this.prisonForm.get('section').valueChanges
      .subscribe(value => {
        if (value === '7') {
          this.prisonForm.addControl('otherSectionName', this.fb.control(''));
        } else {
          this.prisonForm.removeControl('otherSectionName');
        }
      });
    this.prisonForm.get('jobStartDate').valueChanges.subscribe(data => {
      this.prisonForm.get('jobEndDate').updateValueAndValidity();
    });
    this.prisonForm.get('isJobCurrent').valueChanges.subscribe(data => {
      this.prisonForm.get('jobEndDate').updateValueAndValidity();
    });
  }

  private initForm() {
    this.prisonForm = this.fb.group({
      id: this.fb.control(''),
      section: this.fb.control(''),
      jobTitle: this.fb.control(''),
      isJobCurrent: this.fb.control(false),
      jobStartDate: this.fb.control(''),
      jobEndDate: this.fb.control(''),
      skillDescription: this.fb.group({
        question: this.fb.control('What did you do?'),
        answers: this.fb.array([]),
      })
    });
  }

  async loadSkillsAndResolve(id: any) {
    await this.loadAllSkills();
    if (id) {
      this.resolveData(id);
    }
  }

  private resolveData(id: number): void {
    this.workService.getPrisonExperienceDetails(id)
      .subscribe(data => {
        this.prisonForm.get('id').setValue(data.id);
        this.prisonForm.get('section').setValue(data.section);
        this.prisonForm.get('jobTitle').setValue(data.jobTitle);
        this.prisonForm.get('isJobCurrent').setValue(data.isJobCurrent);
        this.prisonForm.get('jobStartDate').setValue(data.jobStartDate);
        this.prisonForm.get('jobEndDate').setValue(data.jobEndDate);
        this.resolveSelectedSkills(data.skillDescription);
        if (data.skillDescription && data.skillDescription.includes('20')) {
          this.prisonForm.addControl('otherSkillDescription', this.fb.control(''));
          this.prisonForm.get('otherSkillDescription').setValue(data.otherSkillDescription);
        }
        if (data.section && '' + data.section === '7') {
          this.prisonForm.addControl('otherSectionName', this.fb.control(''));
          this.prisonForm.get('otherSectionName').setValue(data.otherSectionName);
        }
      });
  }

  private resolveSelectedSkills(ids: string[]) {
    ((this.prisonForm.get('skillDescription') as FormGroup)
      .get('answers') as FormArray).controls.forEach(each => {
        if (ids.includes(each.get('id').value)) {
          each.get('checked').setValue(true);
        }
      });
  }

  private resolveRefData() {
    this.workService.refPrisonSections()
      .subscribe((data: any[]) => {
        this.prisonSections = data;
      });
  }

  async loadAllSkills() {
    await this.workService.getAllSkills()
      .then(res => this.loadAnswers(res));
  }

  private loadAnswers(answers: any[]) {
    const farr = this.prisonForm.get('skillDescription').get('answers') as FormArray;
    this.resetFormArr(farr);
    answers.map(answer => {
      return {
        id: answer.identifier,
        text: answer.description,
        checked: false,
      }
    }).forEach(answer => {
      farr.push(this.singleAnswerForm(answer));
    });
  }

  private singleAnswerForm(data: any) {
    return this.fb.group({
      id: this.fb.control(data.id),
      text: this.fb.control(data.text),
      checked: this.fb.control(data.checked),
    });
  }

  saveAndContinue() {
    this.setValidators(this.prisonForm, PRISONFORM_VALIDATORS_SAVE);
    this.setValidators(this.prisonForm.get('skillDescription') as FormGroup, SKILL_DESCRIPTION_VALIDATORS);
    if (this.prisonForm.valid) {
      const data = this.parseSaveData();
      this.workService.saveAndContinuePrisonWork(data)
        .subscribe(res => {
          this.createNewWorkExp();
        });
    }
    window.scrollTo(0, 0);
  }

  createNewWorkExp() {
    this.resetConditionalForm();
    if (this.createAnother.value === 'N') {
      this.router.navigate(['/employment/cv-builder/guided/experience']);
    } else {
      this.createAnother.setValue('N');
      this.router.navigate([], { queryParams: {} });
    }
  }

  saveAndLater() {
    this.clearAllValidatorsAndErrors(this.prisonForm);
    this.clearAllValidatorsAndErrors(this.prisonForm.get('skillDescription') as FormGroup);
    this.setValidators(this.prisonForm, PRISONFORM_VALIDATORS_DRAFT);
    if (this.prisonForm.valid) {
      const data = this.parseSaveData();
      if (this.isEmptyForm(data)) {
        this.router.navigate(['/employment/cv-builder/guided/experience']);
      } else {
        this.workService.saveAndLaterPrisonWork(data)
          .subscribe(res => {
            this.createNewWorkExp();
          });
      }
    }
    window.scrollTo(0, 0);
  }

  isEmptyForm(data) {
    const filterData = Object.keys(data).filter(name => (data[name]));
    if (filterData.length === 1 && filterData[0] === 'skillDescription') {
      const skillDescription = filterData[0];
      return data[skillDescription].length === 0;
    }
    return false;
  }

  private parseSaveData() {
    const paidWorks = this.prisonForm.value;
    const skills = paidWorks.skillDescription.answers
      .filter(each => each.checked)
      .map(answer => answer.id);
    paidWorks.skillDescription = skills;
    return paidWorks;
  }

  private resetConditionalForm() {
    this.initForm();
    this.loadAllSkills();
  }

  canExit() {
    if (this.prisonForm.dirty) {
      this.confirm.confirm({
        header: 'Confirm',
        message: 'Are you sure you want to leave without saving changes?',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
          this.confirm.choose(true);
        },
        reject: () => {
          this.confirm.choose(false);
        }
      });
      return this.confirm.navigateSelection;
    }
    return true;
  }

  cvSummaryPage() {
    this.router.navigate(['/employment/cv-builder/guided']);
  }

  private clearAllValidatorsAndErrors(form: FormGroup): void {
    Object.keys(form.controls).forEach(name => {
      const control = form.get(name);
      if (control) {
        control.clearValidators();
        control.setErrors(null);
        control.markAsTouched();
      }
    });
  }

  private setValidators(form: FormGroup, validators: any): void {
    Object.keys(form.controls).forEach(name => {
      if (validators[name]) {
        const control = form.get(name);
        if (control) {
          control.setValidators(validators[name]);
          control.updateValueAndValidity();
          control.markAsTouched();
        }
      }
    });
  }

  onKeyJob(event: any) {
    const actualVal = event.target.value;
    const letters = /^[A-Za-z]+$/;
    if (actualVal.match(letters)) {
      this.workService.getJobTitles(event.target.value).subscribe(response => {
        this.jobTitle = response;
      });
    }
  }

  onKeyLocation(event: any) {
    const actualLoc = event.target.value;
    const allowedletters = /^[A-Za-z]+$/;
    if (actualLoc.match(allowedletters)) {
      this.workService.getJobLocation(event.target.value).subscribe(response => {
        this.jobLocation = response;
      });
    }
  }

  resetFormArr(farr: FormArray) {
    while (farr.controls.length !== 0) {
      farr.removeAt(0);
    }
  }
  gotoPrev() {
    this.location.back();
  }

}
